// hooks/usePageVisibility.js
import { useEffect, useState, useRef, useCallback } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import { useNetworkStatus } from './useNetworkStatus';

export const usePageVisibility = ({
  onActive,
  enableWarningSystem = false,
  maxWarnings = 3,
  alertTimer = 10, // Time in seconds for the warning modal
  onFinalAction,
  pauseOnOffline = true, // New option to pause detection when offline
  onPause, // Callback when paused
  onResume // Callback when resumed
}) => {
  const [isActive, setIsActive] = useState(true); // User is active by default
  const warningCountRef = useRef(0);
  const isWarningModalOpenRef = useRef(false);
  const warningTimerRef = useRef(null);
  const resumeTimerRef = useRef(null);

  // Modal state for warning system
  const {
    isOpen: isWarningModalOpen,
    onOpen: onWarningModalOpen,
    onClose: onWarningModalClose
  } = useDisclosure();
  const [warningModalData, setWarningModalData] = useState({
    currentWarning: 0,
    maxWarnings: maxWarnings,
    eventType: '',
    timeRemaining: alertTimer
  });

  // Network status integration
  const { isOnline } = useNetworkStatus();
  const [isPaused, setIsPaused] = useState(false);
  const isPausedRef = useRef(false);

  const clearWarningTimer = useCallback(() => {
    if (warningTimerRef.current) {
      clearInterval(warningTimerRef.current);
      warningTimerRef.current = null;
    }
  }, []);

  const clearResumeTimer = useCallback(() => {
    if (resumeTimerRef.current) {
      clearTimeout(resumeTimerRef.current);
      resumeTimerRef.current = null;
    }
  }, []);

  // Pause and resume methods
  const pause = useCallback(() => {
    if (isPausedRef.current) return;
    isPausedRef.current = true;
    setIsPaused(true);
    clearWarningTimer();
    clearResumeTimer();
    if (isWarningModalOpenRef.current) {
      onWarningModalClose();
      isWarningModalOpenRef.current = false;
    }
    onPause?.();
  }, [clearWarningTimer, clearResumeTimer, onWarningModalClose, onPause]);

  const resume = useCallback(() => {
    if (!isPausedRef.current || !isOnline) return;
    isPausedRef.current = false;
    setIsPaused(false);
    onResume?.();
  }, [isOnline, onResume]);

  const handleUserAcknowledgment = useCallback(() => {
    // User clicked "I Understand" - this acknowledges the warning
    clearWarningTimer();
    onWarningModalClose();
    isWarningModalOpenRef.current = false;
    // Warning is already counted, just close modal
  }, [onWarningModalClose, clearWarningTimer]);

  const handleTimerExpired = useCallback(() => {
    // Timer expired without user acknowledgment - trigger final action
    clearWarningTimer();
    onWarningModalClose();
    isWarningModalOpenRef.current = false;
    onFinalAction?.('alert-timer-expired');
  }, [onFinalAction, onWarningModalClose, clearWarningTimer]);

  const startWarningTimer = useCallback(() => {
    clearWarningTimer(); // Clear any existing timer

    let timeLeft = alertTimer;
    warningTimerRef.current = setInterval(() => {
      timeLeft -= 1;

      setWarningModalData((prev) => ({
        ...prev,
        timeRemaining: timeLeft
      }));

      if (timeLeft <= 0) {
        handleTimerExpired();
      }
    }, 1000);
  }, [clearWarningTimer, handleTimerExpired]);

  const showWarning = useCallback(
    (currentWarning, maxWarnings, eventType) => {
      setWarningModalData({
        currentWarning,
        maxWarnings,
        eventType,
        timeRemaining: alertTimer
      });
      onWarningModalOpen();
      isWarningModalOpenRef.current = true;
      startWarningTimer();
    },
    [onWarningModalOpen, startWarningTimer]
  );

  const handleInactiveEvent = useCallback(
    (eventType) => {
      // Don't trigger warnings if system is paused (e.g., when offline)
      if (isPausedRef.current) {
        return;
      }

      if (enableWarningSystem) {
        // If warning modal is already open, don't trigger another event
        if (isWarningModalOpenRef.current) {
          return;
        }

        warningCountRef.current += 1;

        if (warningCountRef.current >= maxWarnings) {
          // Final action after max warnings reached
          onFinalAction?.(eventType);
        } else {
          // Show warning
          showWarning(warningCountRef.current, maxWarnings, eventType);
        }
      }
    },
    [enableWarningSystem, maxWarnings, showWarning, onFinalAction]
  );

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setIsActive(false);
        handleInactiveEvent('tab-hidden');
      } else {
        setIsActive(true);
        onActive?.('tab-visible');
      }
    };

    const handleWindowBlur = () => {
      // Only trigger if the document is not already hidden
      // This prevents double triggering when switching tabs
      if (!document.hidden) {
        setIsActive(false);
        handleInactiveEvent('window-blur');
      }
    };

    const handleWindowFocus = () => {
      // Only trigger if the document is not hidden
      // This prevents triggering when switching back to a hidden tab
      if (!document.hidden) {
        setIsActive(true);
        onActive?.('window-focus');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [handleInactiveEvent, onActive]);

  // Pause/resume detection based on network status
  useEffect(() => {
    if (pauseOnOffline) {
      if (!isOnline) {
        // Use pause method for consistency
        pause();
      } else {
        // Only set resume timer if currently paused
        if (isPaused) {
          clearResumeTimer();
          resumeTimerRef.current = setTimeout(() => {
            resume();
            resumeTimerRef.current = null;
          }, 20000);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, pauseOnOffline, pause, resume, isPaused, clearResumeTimer]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      clearWarningTimer();
      clearResumeTimer();
    };
  }, [clearWarningTimer, clearResumeTimer]);

  return {
    // Modal state and data for external component to use
    isWarningModalOpen,
    onWarningModalClose: handleUserAcknowledgment,
    warningModalData,
    isPaused,
    pause,
    resume
  };
};
